import { z } from 'zod';
import {
  DEDUP_BLOCK_FLOOR,
  DEDUP_FLOOR_MAX,
  DEDUP_FLOOR_MIN,
  DEDUP_WARN_FLOOR,
} from '../faceDedupRule.js';

/**
 * THE ENROLMENT DUPLICATE FLOORS, AS COMPANY SETTINGS — spread into
 * `FaceRecognitionSettingsSchema` (settings.schema.ts is over its size budget
 * and may only shrink, so the shape lives here).
 *
 * When a new face is enrolled — on a tablet or from the console — every probe
 * takes its best cosine against every OTHER worker's templates, and a quorum of
 * probes (3 of 5) plus the median have to reach a floor
 * (`packages/shared/src/faceDedupRule.ts`):
 *
 *  - `dedupWarnFloor`: at or above it the enrolment still goes ahead, but HR is
 *    told "this face looks like <worker>" and asked to check.
 *  - `dedupBlockFloor`: at or above it the enrolment is refused as a duplicate
 *    (an attended operator or HR may override; the override is audited).
 *
 * A one-photo enrolment has no quorum, so its floors are derived and never
 * lower: max(0.65, warn) and max(0.80, block).
 *
 * WHY THE WARN FLOOR IS A SETTING. Measured on the first production gallery (543
 * workers): two DIFFERENT people's best pairs reach 0.616 at the 99.9th
 * percentile, while the same person's lowest 1% sit at 0.786. A warn floor of
 * 0.60 is inside the impostor tail and raised 257 false "possible duplicate"
 * alerts to HR; ~0.70 sits clear of both tails. The defaults stay at 0.60 / 0.75
 * so nothing changes until an admin decides; `scripts/calibrate-face-thresholds`
 * prints a recommendation from the company's own gallery.
 *
 * Readers clamp as well (`resolveDedupFloors`), so a blob written before these
 * keys existed, or edited by hand, still reads as something safe.
 */
export const FaceDedupFloorsShape = {
  dedupWarnFloor: z.number().min(DEDUP_FLOOR_MIN).max(DEDUP_FLOOR_MAX).default(DEDUP_WARN_FLOOR),
  dedupBlockFloor: z.number().min(DEDUP_FLOOR_MIN).max(DEDUP_FLOOR_MAX).default(DEDUP_BLOCK_FLOOR),
};

/**
 * The write-path refinement: the warn floor must sit BELOW the block floor, or
 * there is no band in which HR is asked to look. Applied where a category is
 * saved (`settings.routes.ts`), not on every read — a read clamps instead, so a
 * hand-edited blob cannot take every settings read down with it.
 */
export function refineDedupFloors(
  value: { dedupWarnFloor?: number; dedupBlockFloor?: number },
  ctx: z.RefinementCtx
): void {
  const warn = value.dedupWarnFloor ?? DEDUP_WARN_FLOOR;
  const block = value.dedupBlockFloor ?? DEDUP_BLOCK_FLOOR;
  if (warn >= block) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dedupWarnFloor'],
      message: 'The duplicate warning score must be lower than the duplicate refusal score.',
    });
  }
}
